import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Award, CheckCircle, XCircle, Search } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

export default function CertificateVerification() {
  const [certificateId, setCertificateId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!certificateId.trim()) return;

    if (!API) {
      setResult({
        valid: false,
        message: 'Backend is not configured yet'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/certificate/verify/${certificateId}`);
      setResult(response.data);
    } catch (error) {
      setResult({
        valid: false,
        message: 'Certificate not found or invalid'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <WhatsAppButton />

      <section className="bg-gradient-to-br from-orange-500 to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Award className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-5xl md:text-6xl font-extrabold font-heading mb-6" data-testid="certificate-title">
              Certificate Verification
            </h1>
            <p className="text-xl font-hindi">
              प्रमाणपत्र सत्यापन प्रणाली
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-orange-50 to-white p-8 md:p-12 rounded-3xl shadow-2xl"
          >
            <h2 className="text-3xl font-bold font-heading text-center mb-8 text-gray-900">
              Verify Your Certificate
            </h2>
            
            <form onSubmit={handleVerify} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Certificate ID
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={certificateId}
                    onChange={(e) => setCertificateId(e.target.value)}
                    className="w-full px-4 py-4 pr-12 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500 transition-colors text-lg"
                    placeholder="Enter Certificate ID (e.g., MKDRP2026001)"
                    data-testid="certificate-input"
                  />
                  <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50"
                data-testid="verify-button"
              >
                {loading ? 'Verifying...' : 'Verify Certificate'}
              </button>
            </form>

            {result && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`mt-8 p-6 rounded-2xl ${
                  result.valid 
                    ? 'bg-green-50 border-2 border-green-200' 
                    : 'bg-red-50 border-2 border-red-200'
                }`}
                data-testid="verification-result"
              >
                <div className="flex items-start gap-4">
                  {result.valid ? (
                    <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0 mt-1" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  )}
                  
                  <div className="flex-1">
                    <h3 className={`text-2xl font-bold mb-3 ${
                      result.valid ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {result.valid ? '✓ Valid Certificate' : '✗ Invalid Certificate'}
                    </h3>
                    
                    {result.valid ? (
                      <div className="space-y-2 text-gray-700">
                        <p className="text-lg">
                          <span className="font-semibold">Student Name:</span> {result.student_name}
                        </p>
                        <p className="text-lg">
                          <span className="font-semibold">Course:</span> {result.course_name}
                        </p>
                        <p className="text-lg">
                          <span className="font-semibold">Issue Date:</span>{' '}
                          {new Date(result.issue_date).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                        <p className="text-lg">
                          <span className="font-semibold">Certificate ID:</span> {result.certificate_id}
                        </p>
                      </div>
                    ) : (
                      <p className="text-red-700 text-lg">
                        {result.message || 'This certificate could not be verified. Please check the Certificate ID and try again.'}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          <div className="mt-12 bg-blue-50 p-8 rounded-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">How to verify?</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Enter your Certificate ID found on your certificate</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Click on "Verify Certificate" button</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>View your certificate details if valid</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
